import { Client, portalApi } from '@central/api';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

export const client = new Client({ baseUrl: '/api' });
export const api = portalApi(client);

export const meQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: () => api.auth.me(),
  staleTime: 60_000,
  retry: false,
});

export const homeQuery = queryOptions({ queryKey: ['home'], queryFn: () => api.home() });

export const deploymentsQuery = queryOptions({
  queryKey: ['deployments'],
  queryFn: () => api.deployments.list(),
});

export const deploymentQuery = (id: string) =>
  queryOptions({
    queryKey: ['deployments', id],
    queryFn: () => api.deployments.get(id),
    // A 404 is an answer, not a transient failure.
    retry: false,
  });

export const licensesQuery = queryOptions({
  queryKey: ['licenses'],
  queryFn: () => api.licenses.list(),
});

export const releasesQuery = queryOptions({
  queryKey: ['releases'],
  queryFn: () => api.releases.list(),
});

export const notificationsQuery = queryOptions({
  queryKey: ['notifications'],
  queryFn: () => api.notifications.list(),
});

export const unreadCountQuery = queryOptions({
  queryKey: ['threads', 'unread-count'],
  queryFn: () => api.threads.unreadCount(),
  refetchInterval: 60_000,
});

/** Pages of a thread, newest page first; `fetchNextPage` loads the page before the last one. */
export const threadQuery = (id: string) =>
  infiniteQueryOptions({
    queryKey: ['threads', id],
    queryFn: ({ pageParam }) => api.threads.get(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.earlierCursor,
    retry: false,
  });

/** Who is notified about a thread (its customer-side watchers). */
export const watchersQuery = (threadId: string) =>
  queryOptions({
    queryKey: ['threads', threadId, 'watchers'],
    queryFn: () => api.threads.watchers(threadId),
    retry: false,
  });

export const credentialsQuery = queryOptions({
  queryKey: ['auth', 'credentials'],
  queryFn: () => api.auth.listCredentials(),
});

export const trustedBrowsersQuery = queryOptions({
  queryKey: ['auth', 'trusted-browsers'],
  queryFn: () => api.auth.trustedBrowsers(),
});

export const settingsQuery = queryOptions({
  queryKey: ['settings'],
  queryFn: () => api.settings.get(),
});

export const notificationPrefsQuery = queryOptions({
  queryKey: ['me', 'notifications'],
  queryFn: () => api.notificationPrefs.get(),
});

export const organizationQuery = queryOptions({
  queryKey: ['organization'],
  queryFn: () => api.organization(),
});

/**
 * What attaching from a deployment would send now. Only with `logs` are its
 * logs read, an audited log query; each answer is kept a minute.
 */
export const attachPreviewQuery = (environmentId: string, services: string[], logs: boolean) =>
  queryOptions({
    queryKey: ['threads', 'attach-preview', environmentId, logs ? services : [], logs],
    queryFn: () => api.threads.attachPreview(environmentId, services, logs),
    staleTime: 60_000,
    retry: false,
  });

export const teamQuery = queryOptions({ queryKey: ['team'], queryFn: () => api.team.get() });

export const sessionsQuery = queryOptions({
  queryKey: ['me', 'sessions'],
  queryFn: () => api.sessions.list(),
});
